import ReactSignatureCanvas from 'react-signature-canvas';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useDrawHistory } from '../hooks/useDrawHistory';

import PlaceholderSignature from './PlaceholderSignature';

const DrawSignature = ({
  active,
  value,
  onChange,
  onHistoryChange,
  penColor = '#000000',
  canvasHeight = 200,
  containerWidth = 0,
  handleRef,
  'data-qa': dataQa
}) => {
  /** @type {React.MutableRefObject<any>} */
  const sigPadRef = useRef(null);
  /** @type {React.MutableRefObject<string | null>} */
  const syncedValueRef = useRef(null);
  /** @type {React.MutableRefObject<{ url: string, image: HTMLImageElement } | null>} */
  const imageCacheRef = useRef(null);
  const drawRequestRef = useRef(0);
  const history = useDrawHistory(sigPadRef);
  const { setBaseline, reset, undo, redo, capture, canUndo, canRedo } = history;
  const [hasContent, setHasContent] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(0);

  const getCurrentPng = useCallback(
    () =>
      sigPadRef.current && !sigPadRef.current.isEmpty()
        ? sigPadRef.current.getCanvas().toDataURL('image/png')
        : null,
    []
  );

  const primeImage = useCallback((dataUrl) => {
    if (!dataUrl || imageCacheRef.current?.url === dataUrl) return;
    const image = new Image();
    image.onload = () => {
      imageCacheRef.current = { url: dataUrl, image };
    };
    image.src = dataUrl;
  }, []);

  const paintImage = useCallback((image, token) => {
    if (drawRequestRef.current !== token || !sigPadRef.current) return;
    const canvas = sigPadRef.current.getCanvas();
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const fit =
      image.width && image.height
        ? Math.min(canvas.width / image.width, canvas.height / image.height, 1)
        : 1;
    const drawWidth = image.width * fit;
    const drawHeight = image.height * fit;
    ctx.drawImage(
      image,
      (canvas.width - drawWidth) / 2,
      (canvas.height - drawHeight) / 2,
      drawWidth,
      drawHeight
    );
    const pad = sigPadRef.current.getSignaturePad?.();
    if (pad) pad._isEmpty = false;
    setHasContent(true);
  }, []);

  const renderValue = useCallback(
    (dataUrl) => {
      if (!sigPadRef.current) return;
      const token = drawRequestRef.current + 1;
      drawRequestRef.current = token;

      if (!dataUrl) {
        const canvas = sigPadRef.current.getCanvas();
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        const pad = sigPadRef.current.getSignaturePad?.();
        if (pad) pad._isEmpty = true;
        setHasContent(false);
        return;
      }

      const cached = imageCacheRef.current;
      if (cached?.url === dataUrl && cached.image.complete && cached.image.naturalWidth) {
        paintImage(cached.image, token);
        return;
      }

      const image = new Image();
      image.onload = () => {
        imageCacheRef.current = { url: dataUrl, image };
        paintImage(image, token);
      };
      image.onerror = () => {
        if (drawRequestRef.current === token) setHasContent(false);
      };
      image.src = dataUrl;
    },
    [paintImage]
  );

  useEffect(() => {
    onHistoryChange?.({ canUndo, canRedo });
  }, [canUndo, canRedo, onHistoryChange]);

  useEffect(() => {
    if (!containerWidth || containerWidth === canvasWidth) return;
    setCanvasWidth(containerWidth);
  }, [containerWidth, canvasWidth]);

  useLayoutEffect(() => {
    if (!canvasWidth || !sigPadRef.current) return;
    renderValue(syncedValueRef.current);
  }, [canvasWidth, renderValue]);

  useEffect(() => {
    if (!sigPadRef.current || !canvasWidth || !active) return;

    if (value === undefined || value === syncedValueRef.current) return;

    const hadSyncedValue = !!syncedValueRef.current;
    syncedValueRef.current = value || null;

    if (!value) {
      if (hadSyncedValue) {
        reset();
        renderValue(null);
      }
      return;
    }

    primeImage(value);
    renderValue(value);
    setBaseline(value);
  }, [active, value, canvasWidth, renderValue, primeImage, reset, setBaseline]);

  useEffect(() => {
    if (!handleRef) return undefined;
    handleRef.current = {
      getValue: () => getCurrentPng(),
      clear: () => {
        syncedValueRef.current = null;
        reset();
        renderValue(null);
      },
      undo: () => {
        const result = undo();
        syncedValueRef.current = result;
        setHasContent(!!result);
        return result;
      },
      redo: () => {
        const result = redo();
        syncedValueRef.current = result;
        setHasContent(!!result);
        return result;
      }
    };
    return () => {
      if (handleRef) handleRef.current = null;
    };
  }, [handleRef, getCurrentPng, renderValue, reset, undo, redo]);

  const handleEnd = () => {
    capture();
    const next = getCurrentPng();
    syncedValueRef.current = next;
    primeImage(next);
    setHasContent(!!next);
    if (active) onChange?.(next);
  };

  return (
    <div
      className="relative min-w-0 overflow-hidden rounded-md border border-dashed border-neutral-500 bg-primary-10"
      style={{ height: canvasHeight }}>
      {canvasWidth > 0 && (
        <ReactSignatureCanvas
          ref={sigPadRef}
          clearOnResize={false}
          penColor={penColor}
          minWidth={0.8}
          maxWidth={2.2}
          velocityFilterWeight={0.6}
          throttle={8}
          minDistance={1}
          dotSize={1.2}
          onBegin={() => {
            setHasContent(true);
          }}
          onEnd={handleEnd}
          canvasProps={{
            width: canvasWidth,
            height: canvasHeight,
            'data-qa': active ? dataQa : undefined,
            style: {
              width: canvasWidth,
              height: canvasHeight,
              maxWidth: 'none',
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'transparent',
              border: 'none',
              borderRadius: 0,
              display: 'block'
            }
          }}
        />
      )}
      {!hasContent && <PlaceholderSignature canvasHeight={canvasHeight} />}
    </div>
  );
};

export default DrawSignature;
